import React, {ReactNode, useMemo, useState} from 'react';
import {Store} from 'redux';
import {ErrorHandler, showErrorDialog} from '../errorhandler/ErrorHandler';
import {
   BorderPanel,
   LocalizationConfig,
   Text,
   Tooltip,
   VerticalPanel
} from '@unifocus/react-components';
import {Icons} from '@unifocus/react-icons';
import {ApplicationContext} from '../applicationcontext';
import {getEnvironment} from '@unifocus/js-utils';
import {VERSION} from '../version';
import classnames from 'classnames';

/**
 * {@docCategory Application}
 */
export interface ApplicationProps {
   /**
    * Set the redux store. This property takes priority over the storeProvider function.
    * If you don't specify a store or storeProvider, then the redux provider will not be configured.
    */
   store?: Store;

   /**
    * Returns the redux store, when using redux. If you use the route, note that the route is always
    * the base part of the route. For example, if the URL looks like "https://test.unifocus.com/test/index.html/#/main/123"
    * then the route passed to the function will be "/main".
    *
    * If you don't specify a store or storeProvider, then the redux provider will not be configured.
    *
    * @param route - the route string
    */
   storeProvider?: (route: string) => Store;

   /**
    * Determines if the application is a mobile application.
    * @defaultvalue false
    */
   mobile?: boolean;

   /**
    * The children.
    */
   children?: ReactNode;

   /**
    * Show the standard unifocus background image. Setting the value to true or 'dark'
    * will show the dark background. Setting the value to 'light' will show the light background.
    */
   background?: true | 'light' | 'dark';
}

export const Application = (props: ApplicationProps) => {
   (getEnvironment() as any).mobile = props.mobile === undefined ? false : props.mobile;

   const preReleaseHeader = useMemo(() => <PreReleaseHeader/>, []);

   const className = classnames({
                                   'uf-application-main-panel': true,
                                   'uf-application-mobile-main-panel': props.mobile,
                                   'uf-application-background': props.background,
                                   'uf-application-background-dark': props.background || props.background === 'dark',
                                   'uf-application-background-light': props.background === 'light'
                                });
   return (
      <LocalizationConfig>
         <ErrorHandler>
            <ApplicationContext
               store={props.store}
               storeProvider={props.storeProvider}
               errorHandler={errorHandler}
            >
               <BorderPanel
                  className={className}
                  top={preReleaseHeader}
               >
                  {content(props)}
               </BorderPanel>
            </ApplicationContext>
         </ErrorHandler>
      </LocalizationConfig>
   );
};

const content = (props: ApplicationProps) => {
   if (props.mobile && getEnvironment().deviceType === 'DESKTOP') {
      return (
         <VerticalPanel className={'uf-application-mobile-content-container'}>
            {props.children}
         </VerticalPanel>
      );
   }

   return props.children;
};

const errorHandler = {
   showError: showErrorDialog
};

const isPreRelease = () => {
   return VERSION.indexOf('-pre') >= 0;
};

const preReleaseText = 'This application is using a pre-release version of the unifocus-ui-platform packages.';

const PreReleaseHeader = () => {
   const [visible, setVisible] = useState(isPreRelease());

   if (visible) {
      const preRelease = <Tooltip title={preReleaseText}><Text strong={true} ellipsis={true}
                                                               className={'uf-pre-release-text'}>
         {preReleaseText}
      </Text></Tooltip>;

      const closeButton = <div
         className={'uf-application-pre-release-header-close-icon'}
         onClick={() => {
            setVisible(false);
         }}
      >
         <Icons.Close/>
      </div>;

      return (
         <VerticalPanel
            className={'uf-application-pre-release-header'}
            padding={true}
            height={'max-content'}
         >
            <BorderPanel
               right={closeButton}
            >
               {preRelease}
            </BorderPanel>
         </VerticalPanel>
      );
   }

   return null;
};
